/**
 * Transcripción virtualizada (SDD §7.2): hablantes y tiempos, búsqueda dentro
 * de la reunión, salto desde la evidencia de las notas y reproducción opcional
 * de la copia local del audio (eco no guarda el audio: el usuario elige el
 * archivo de su computador y se sincroniza con el texto).
 */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type CSSProperties
} from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import type { TranscriptSegment } from '@eco/shared'
import { formatDuration } from '../lib/format'
import { IconPause, IconPlay } from './icons'
import { findMatches, segmentAt } from '../lib/transcript'

export interface TranscriptHandle {
  jumpTo(ms: number): void
}

interface Props {
  segments: TranscriptSegment[]
  offsetOthersMs: number
  onSpeakerClick?: (speaker: string) => void
  /** Momentos marcados durante la grabación (ms). */
  marcas?: number[]
}

interface LocalAudio {
  el: HTMLAudioElement
  url: string
  /** 'others' se desplaza por el offset de pistas; 'main' marca el tiempo. */
  role: 'main' | 'others'
  name: string
}

export const TranscriptPanel = forwardRef<TranscriptHandle, Props>(function TranscriptPanel(
  { segments, offsetOthersMs, onSpeakerClick, marcas = [] },
  ref
) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [flash, setFlash] = useState<number | null>(null)
  const [q, setQ] = useState('')
  const [matchIdx, setMatchIdx] = useState(0)
  const [audios, setAudios] = useState<LocalAudio[]>([])
  const [playing, setPlaying] = useState(false)
  const [nowMs, setNowMs] = useState<number | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  // Orden de aparición de cada hablante (para su color)
  const orden = useMemo(() => {
    const m = new Map<string, number>()
    for (const seg of segments)
      if (seg.speaker !== 'Yo' && !m.has(seg.speaker)) m.set(seg.speaker, m.size)
    return m
  }, [segments])

  const virt = useVirtualizer({
    count: segments.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 64,
    overscan: 12
  })

  const matches = useMemo(() => findMatches(segments, q), [segments, q])
  useEffect(() => setMatchIdx(0), [q])
  // Solo cuando cambia la búsqueda o el resultado elegido: el polling no debe mover la vista
  const scrolledFor = useRef('')
  useEffect(() => {
    const i = matches[matchIdx]
    const key = `${q}:${matchIdx}`
    if (i === undefined || scrolledFor.current === key) return
    scrolledFor.current = key
    virt.scrollToIndex(i, { align: 'center' })
  }, [matches, matchIdx, q, virt])

  function jumpTo(ms: number) {
    const i = segmentAt(segments, ms)
    if (i < 0) return
    virt.scrollToIndex(i, { align: 'center' })
    setFlash(i)
    setTimeout(() => setFlash((f) => (f === i ? null : f)), 2200)
    seek(ms)
  }
  useImperativeHandle(ref, () => ({ jumpTo }))

  // --- copia local ---------------------------------------------------------
  function seek(ms: number) {
    for (const a of audios)
      a.el.currentTime = Math.max(0, (ms - (a.role === 'others' ? offsetOthersMs : 0)) / 1000)
  }
  function toggle() {
    if (!audios.length) return fileRef.current?.click()
    if (playing) audios.forEach((a) => a.el.pause())
    else void Promise.all(audios.map((a) => a.el.play())).catch(() => undefined)
    setPlaying(!playing)
  }
  function load(files: FileList | null) {
    audios.forEach((a) => {
      a.el.pause()
      URL.revokeObjectURL(a.url)
    })
    const list = [...(files ?? [])].slice(0, 2)
    const next: LocalAudio[] = list.map((f, i) => {
      const url = URL.createObjectURL(f)
      const el = new Audio(url)
      const others =
        list.length === 2 &&
        (/los-demas|others/i.test(f.name) || (i === 1 && !/los-demas|others/i.test(list[0]!.name)))
      return { el, url, role: others ? 'others' : 'main', name: f.name }
    })
    const main = next.find((a) => a.role === 'main') ?? next[0]
    if (main) {
      main.el.ontimeupdate = () => setNowMs(main.el.currentTime * 1000)
      main.el.onended = () => setPlaying(false)
    }
    setAudios(next)
    setPlaying(false)
  }
  useEffect(
    () => () =>
      audios.forEach((a) => {
        a.el.pause()
        URL.revokeObjectURL(a.url)
      }),
    [audios]
  )
  const playingIdx = nowMs === null ? -1 : segmentAt(segments, nowMs)
  useEffect(() => {
    if (playing && playingIdx >= 0) virt.scrollToIndex(playingIdx, { align: 'auto' })
  }, [playing, playingIdx, virt])

  const matchSet = useMemo(() => new Set(matches), [matches])
  const markSet = useMemo(
    () => new Set(marcas.map((m) => segmentAt(segments, m))),
    [marcas, segments]
  )

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="row wrap no-print">
        <label className="search grow" style={{ minWidth: 180 }}>
          <span className="sr">Buscar en la transcripción</span>
          <input
            type="search"
            placeholder="Buscar en esta reunión…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && matches.length)
                setMatchIdx((i) => (i + (e.shiftKey ? -1 + matches.length : 1)) % matches.length)
            }}
          />
          {q && (
            <span className="faint mono" style={{ fontSize: 11 }} aria-live="polite">
              {matches.length ? `${matchIdx + 1}/${matches.length}` : '0'}
            </span>
          )}
        </label>
        <button
          className="btn sm"
          onClick={toggle}
          title="Elige la copia de audio que eco guardó en tu computador"
        >
          {audios.length ? (
            playing ? (
              <>
                <IconPause /> Pausa
              </>
            ) : (
              <>
                <IconPlay /> Reproducir
              </>
            )
          ) : (
            <>
              <IconPlay /> Copia local
            </>
          )}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="audio/*,video/webm"
          multiple
          hidden
          onChange={(e) => load(e.target.files)}
        />
      </div>
      {audios.length > 0 && (
        <div className="faint" style={{ fontSize: 12 }}>
          Reproduciendo desde tu equipo: {audios.map((a) => a.name).join(' + ')}{' '}
          <button className="iconbtn" onClick={() => fileRef.current?.click()}>
            Cambiar
          </button>
        </div>
      )}
      <div
        ref={scrollRef}
        className="transcript"
        role="list"
        aria-label="Transcripción"
        tabIndex={0}
      >
        <div style={{ height: virt.getTotalSize(), position: 'relative' }}>
          {virt.getVirtualItems().map((v) => {
            const s = segments[v.index]!
            const prev = segments[v.index - 1]
            const cont = prev?.speaker === s.speaker && s.inicioMs - prev.finMs < 30_000
            return (
              <div
                key={v.key}
                ref={virt.measureElement}
                data-index={v.index}
                role="listitem"
                className={`seg ${cont ? 'cont' : ''} ${flash === v.index ? 'flash' : ''} ${playingIdx === v.index ? 'playing' : ''}`}
                style={
                  {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    transform: `translateY(${v.start}px)`,
                    '--spk': speakerColor(s.speaker, orden)
                  } as CSSProperties
                }
              >
                {!cont && (
                  <div>
                    <button
                      className="iconbtn who"
                      style={{ padding: 0, color: 'var(--text)' }}
                      onClick={() => onSpeakerClick?.(s.speaker)}
                    >
                      {s.speaker}
                    </button>
                    <button
                      className="ts"
                      onClick={() => jumpTo(s.inicioMs)}
                      aria-label={`Ir a ${formatDuration(s.inicioMs)}`}
                    >
                      {formatDuration(s.inicioMs)}
                    </button>
                  </div>
                )}
                <div className="tx">
                  {markSet.has(v.index) && (
                    <span
                      className="mark-star"
                      title="Momento marcado durante la reunión"
                      aria-label="Momento marcado"
                    >
                      ★{' '}
                    </span>
                  )}
                  {matchSet.has(v.index) ? <Marked text={s.texto} q={q} /> : s.texto}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
})

function Marked({ text, q }: { text: string; q: string }) {
  const parts = splitByQuery(text, q)
  return (
    <span className="hit">
      {parts.map((p, i) => (p.hit ? <mark key={i}>{p.text}</mark> : <span key={i}>{p.text}</span>))}
    </span>
  )
}

function splitByQuery(text: string, q: string) {
  const needle = q.trim()
  if (!needle) return [{ text, hit: false }]
  const norm = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()
  const hay = norm(text)
  const n = norm(needle)
  const out: { text: string; hit: boolean }[] = []
  let i = 0
  // NFD sin marcas conserva la longitud en español salvo casos raros: se valida
  if (hay.length !== text.length) return [{ text, hit: false }]
  for (let j = hay.indexOf(n); j >= 0; j = hay.indexOf(n, i)) {
    if (j > i) out.push({ text: text.slice(i, j), hit: false })
    out.push({ text: text.slice(j, j + n.length), hit: true })
    i = j + n.length
  }
  if (i < text.length) out.push({ text: text.slice(i), hit: false })
  return out
}

/**
 * Color estable por hablante: «Yo» siempre terracota; los demás, en orden de
 * aparición, con la paleta cálida de la marca (--spk-1…5).
 */
function speakerColor(speaker: string, orden: Map<string, number>): string {
  if (speaker === 'Yo') return 'var(--spk-0)'
  const i = orden.get(speaker) ?? 0
  return `var(--spk-${(i % 5) + 1})`
}
